import { DocumentPage } from 'src/components';
import { getEnvByName } from 'src/utils';

const mobilePrivacyPolicyLink = getEnvByName(
  'VITE_MOBILE_PRIVACY_POLICY_LINK',
);

const PrivacyPolicy = (): JSX.Element => {
  return (
    <DocumentPage>
      <h1>Privacy Policy</h1>
      <p>
        We at Infinity Labs R&amp;D. Ltd. (together with its related companies
        &ndash; &quot;ILRD&quot; or &quot;us&quot;) operate training courses and
        develop software for efficient communication and collaboration (the
        &ldquo;
        <b>Applications</b>&rdquo;). This Privacy Policy describes how we
        collect, process, and share personal information (&ldquo;
        <b>Information</b>&rdquo;) relating to the Applications users (&ldquo;
        <b>Data Subjects</b>&rdquo;), in particular, this privacy policy
        describes the following:&nbsp;
      </p>
      <ol>
        <li>What information do we collect?</li>
        <li>What purposes is this information used for?</li>
        <li>Who will the information be provided to?</li>
        <li>Where is the information stored?</li>
        <li>How long will the information be kept?</li>
        <li>How do we use cookies and monitoring technologies?</li>
        <li>How will we get in touch with you?</li>
        <li>How is the information secured?</li>
        <li>How can you exercise your rights in relation to information?</li>
        <li>General notices and contact information.</li>
        <li>
          Limited Use disclosure that complies with the Google API Services User
          Data Policy.
        </li>
      </ol>
      <p>
        Please read the privacy policy carefully and make sure that you
        understand and agree to it. To clarify, you are not under any legal
        obligation to provide us with any information concerning you. If you do
        not agree to the following, please refrain from providing us with
        information concerning you and refrain from using the
        Applications.&nbsp;
      </p>
      <h2>
        <b>1. What information do we collect?</b>
      </h2>
      <ol>
        <li>
          a. We may collect information about you, including certain details
          about you or the device and software you used. Depending on the
          circumstances and the information you have made available to us, these
          details may include the following:&nbsp;
        </li>
      </ol>
      <ul>
        <li>
          Usage data on the Applications&ndash; Technical usage data, aggregate
          statistical information and activity records (such as pages viewed,
          mouse clicks and similar interactions), connection and disconnection
          times from the site, IP address and connectivity data, device data
          (such as device type used to connect and operating system, advertising
          ID , browser type and version number, general location, time zone and
          language settings), as well as the cookie and pixel files installed on
          the device or used in connection with it (as specified in section 6).
        </li>
        <li>
          Business information &ndash; Name, phone, email address, organization
          and position, as well as experience, preferences and professional
          interests.
        </li>
        <li>
          Direct inquiries and communication with us &ndash; Details and
          conversations (written or recorded) and correspondence given to us
          through online forms, orally, by email and/or in chats with various
          departments at ILRD.
        </li>
      </ul>
      <p>
        We collect said information directly from you and/or due to your
        interaction with the Applications, as well as through third parties such
        as our service providers (as detailed in section 3) or our business
        partners, in connection with business initiatives and activities and
        events that we organize or participate in.
      </p>
      <ol>
        <li>
          b. Information about Data Subjects: you may provide us with
          information (or we will receive access to it in another way), which
          will usually include the following:&nbsp;
        </li>
      </ol>
      <ul>
        <li>
          Contact and identification information &ndash; Name, email address,
          home address, mobile phone number, username and encrypted password.
        </li>
        <li>
          Education &ndash; Psychometric score, academic institution and fields
          of study, year of graduation, grade point average.
        </li>
        <li>
          Employment experience &ndash; CV, including qualifications and
          professional experience, courses and certifications, as well as date
          of birth, ID number, marital status, military service and hobbies (if
          you chose to list them in the CV).
        </li>
        <li>
          Public data &ndash; Data that is publicly available, such as activity
          on social networks (e.g., LinkedIn).
        </li>
        <li>
          Test data &ndash; Information collected during your participation in
          personality and competency tests that examine your suitability for our
          training courses and your potential future integration in working with
          our clients, including answers given to us in writing and orally, in
          response to exam questionnaires and in telephone and face-to-face
          interviews, data on the devices used and software made available to
          you during the tests (such as response times, typing patterns, mouse
          clicks and similar interactions), and their analysis by our
          recruitment team.
        </li>
        <li>
          AI assistant interactions &ndash; If you use an AI-powered assistant or other AI-enabled feature made available through the Applications, we may collect the messages, questions, prompts and other content you submit to the assistant, the assistant&apos;s responses, any feedback you provide regarding those responses, and related usage and technical information. Such interactions may include personal information if you choose to include it in your messages.
        </li>
        <li>
          Direct inquiries and communications with us &ndash; Details and
          conversations (written or oral) and correspondence provided to us
          through online forms, orally, by email and/or in chats with various
          departments at ILRD (such as our recruitment team).
        </li>
        <li>
          Any other information you may provide us with during and in connection
          with the tests.
        </li>
      </ul>
      <p>
        We collect said information directly from you, as part of your
        registration for tests and during and/or due to your interaction with
        the Applications, as well as through third parties such as our service
        providers (as specified in section 3) and a friend-brings-a friend
        referrals (as specified in section 7).&nbsp;
      </p>
      <h2>
        <b>2. What purposes is this information used for?</b>
      </h2>
      <p>We collect information and use it for the following purposes:&nbsp;</p>
      <ul>
        <li>Operation of the Applications and its maintenance.</li>
        <li>
          Developing and improving the Applications and customizing your and
          other users&rsquo; experience based on common or individual
          preferences, experiences and problems.
        </li>
        <li>
          Contacting you at your request and/or through general or personalized
          messages related to our services, as well as through marketing
          messages that may be of interest to you (as specified in section 7).
        </li>
        <li>
          Production and presentation of more effective advertisements and
          marketing content, including advertising based on context, behaviour
          and interests, from the activity of visitors to the Applications,
          their preferences and other data available to us or our business
          partners.
        </li>
        <li>
          Generating insights related to our target audiences and which allow us
          to maintain and improve our marketing and advertising activities, as
          well as our services.
        </li>
        <li>
          Information security and protection of our business activities,
          including to prevent or reduce the risk of fraud, or any other
          prohibited or illegal activity.
        </li>
        <li>
          Creating aggregate statistical data or data that has been aggregated
          in a way that has made it anonymous and non-identifiable or that
          cannot be linked to an identified person by reasonable means, even
          indirectly, which we or our business partners may use to provide and
          improve our services, or for any other purpose.
        </li>
        <li>
          Execution and enforcement of agreements as well as settlement of legal
          disputes to which ILRD is a party.
        </li>
        <li>
          Compliance with any requirement that applies to us by virtue of the
          provisions of the law and the competent authorities, including court
          orders, and any additional action that may require the use of
          information concerning you and for which we will obtain your consent
          in advance in accordance with the law.
        </li>
      </ul>
      <p>
        In addition to the purposes listed above, information pertaining to
        candidates for training only is collected and used by us for the
        following purposes:&nbsp;
      </p>
      <ul>
        <li>
          Verifying your identity for the purpose of providing access to the
          ILRD offices and its systems as part of the tests.
        </li>
        <li>
          Assessing your professional experience, personality and skills in
          order to examine, consider and verify your suitability for the
          training course for which you are a candidate and the potential of
          your future integration into work with our clients.
        </li>
        <li>
          Advancing your selection procedure for the relevant training course
          and contacting you in connection with it (as specified in section 7).
        </li>
        <li>
          Continuing to develop, adapt and improve the selection procedure, the
          tests and other products and services based on preferences,
          experiences and common or individual difficulties of candidates for
          training.
        </li>
        <li>
          Where the Applications include an AI-powered assistant, we may use interaction data to provide and operate the assistant; assist users with the learning experience; troubleshoot errors and technical issues; monitor and evaluate the quality, accuracy, safety and effectiveness of the assistant; detect and prevent misuse; and develop and improve the assistant, the Applications and our learning and support processes. For these purposes, selected conversations may be reviewed by authorized ILRD personnel. We may also use conversation data to evaluate AI systems used in connection with the Applications.
        </li>
      </ul>
      <h2>
        <b>3. Who will the information be provided to?</b>
      </h2>
      <p>
        For the purposes detailed in this privacy policy, we may provide
        information concerning you to third parties and other recipients, as
        described below:&nbsp;
      </p>
      <ul>
        <li>
          &nbsp; &nbsp; Service providers &ndash; We use external companies and
          other third parties who provide us with solutions and services that
          complement, enable and improve our services or support our activities.
          These may include artificial intelligence and machine-learning service providers, information storage services and servers, content delivery networks (CDNs), information security and cyber protection, fraud detection and prevention, recruitment systems and related
          services, task and project management system providers, monitoring and
          distribution of electronic mail, advertising and marketing, social
          media, as well as consultants in the field of business, legal,
          finance, etc. (&quot;
          <b>the service providers</b>
          &quot;). The service providers may have access to information
          concerning you depending on the service they provide and the specific
          purpose for which we contracted with them, and they will be allowed to
          use this information exclusively for that purpose.
        </li>
        <li>
          &nbsp; &nbsp; Infinity Labs R&amp;D employees &ndash; Some of our
          employees may have access to certain information about you, such as
          your identity and contact information, and this is only to the extent
          required to fulfill their duties and in accordance with their need for
          information in order to carry it out ( for example, the resumes of
          candidates for training will be given to employees in the recruitment
          team ).
        </li>
        <li>
          &nbsp; &nbsp; Business partners &ndash; We use business partners, such
          as third parties who provide professional services related to our
          services, for the purpose of locating and realizing opportunities for
          business growth and new target audiences. In these cases, we may share
          contact details and relevant business information relating to you with
          the relevant business partner, so that they can contact you for these
          purposes. In addition, if you are a candidate for training and have
          been referred to us by a business partner, we may provide that partner
          with updates regarding the status of your acceptance and training .
          Please note that if you come into contact with any of our business
          partners, any aspect of that interaction that is not directly related
          to our services or facilitated by us is beyond our control and
          responsibility, and this privacy policy does not apply to it. In any
          such case, we encourage you to carefully read the privacy policy of
          the relevant business partner.
        </li>
        <li>
          &nbsp; &nbsp; Affiliated Companies and Organizational Changes &ndash;
          We may disclose information about you to companies affiliated with
          ILRD (for the purposes outlined in this Privacy Policy. In addition,
          if there is any future change in the ownership or control of ILRD,
          including as a result of a merger or purchase of its assets, ILRD (or
          any of its related companies) will be entitled to provide information
          concerning you to the parties involved in such an event, as this may
          be reasonably required in its context. If we believe that sharing such
          information may materially affect your privacy in the information that
          will be in our possession or under our control at that time, we will
          inform you of such an event and the options open to you in connection
          with it by email, through other suitable means of communication
          available to us, and/or in a prominent notice on the Applications.
        </li>
        <li>
          &nbsp; &nbsp; Compliance with the instructions of the law and the
          courts &ndash; We may provide information concerning you to authorized
          authorities and enforcement agencies or grant them access to it after
          you have been given prior notice or without it, depending on the law
          applicable to us in the circumstances of the case &ndash; if we
          believe in good faith that (a) we have an obligation legal to do so
          (for example, by virtue of a judicial order or a binding requirement
          of a competent authority); and/or (b) this is required for the purpose
          of investigating, preventing or thwarting illegal activity and other
          crimes or crimes, suspected or known.
        </li>
        <li>
          &nbsp; &nbsp; Protection of rights and safety &ndash; We may disclose
          information concerning you to other parties, without prior notice, if
          we believe in good faith that this will help protect the rights,
          personal safety or property of ILRD, its employees, its service
          providers, its customers, its business partners, the users of its
          applications or any other person or entity. To clarify, we may share
          information concerning you in additional ways, if you have given your
          consent to this, if we have a legal obligation to do so, or if we have
          made the information anonymous in a way that does not allow you to be
          identified by reasonable means, even if indirectly.
        </li>
      </ul>
      <p>&nbsp;</p>
      <h2>
        <b>4. Where is the information stored?</b>
      </h2>
      <p>
        The information concerning you may be stored in the databases owned by
        ILRD, as well as by our service providers (as specified in section 3)
        who may hold the information or gain access to it in order to provide us
        with certain services &ndash; both within the borders of the State of
        Israel and in other countries, in accordance with the requirements of
        the law. &nbsp; Although privacy protection laws may vary from country
        to country, ILRD, its affiliated companies and service providers on its
        behalf, are obligated to protect the information concerning you in
        accordance with this privacy policy as stated, the standards accepted in
        the market, as well as appropriate legal and contractual mechanisms that
        require adequate protection of information according to the law in
        Israel &ndash; regardless of lesser legal requirements that may apply in
        the country which the information is transferred to.
      </p>
      <h2>
        <b>5. How long will the information be kept?</b>
      </h2>
      <p>
        We may keep information concerning you as long as it is reasonably
        required for the purpose(s) for which that information was collected or
        created by us (as specified in section 2), and no later than that
        &ndash; all in accordance with our information retention policy and
        subject to legal requirements. Unless otherwise provided by law, we are
        not obligated to keep your information for any fixed period of time, and
        we may securely delete it at any time and for any reason. &nbsp; If you
        are a training program applicant, we may retain certain information
        about you even after your application has been accepted or rejected.
        This is as a reference for additional nominations you submit in the
        future and in case you are accepted for the training, for additional
        purposes related to your participation as a student and our business
        needs related to that, and as reasonably required for the purposes
        specified in this privacy policy.&nbsp;
      </p>
      <p>
        Unless a different period is required or permitted by applicable law, identifiable conversation data generated through our AI-powered assistant will generally be retained for up to 24 months, after which it will be deleted or de-identified, unless continued retention is reasonably necessary for information security, fraud or abuse prevention, resolving disputes, or establishing, exercising or defending legal claims.
        De-identified information that can no longer reasonably be associated with an identifiable individual may be retained for longer periods for statistical, research, analytics and service-improvement purposes.
      </p>
      <h2>
        <b>6. How do we use cookies and monitoring technologies?</b>
      </h2>
      <p>
        A &quot;cookie&quot; file is a small text file that is stored on your
        device&apos;s browser when you visit the Applications, allowing data to
        be collected about your browsing habits. Some of the cookies will be
        removed from your device when you leave the site or close the browser,
        and others will be saved in your device&apos;s memory. We use both
        types.&nbsp; the Applications use cookies, pixels, anonymous identifiers
        and other monitoring technologies for the purpose of collecting data for
        the duration of your visit, the pages you visited and from where you
        accessed our web applications, your browsing habits, your internet
        provider&apos;s details, IP address, general location of the device,
        etc. This data is collected to help us operate the Applications, secure
        them and ensure their proper operation, including monitoring, evaluating
        and analyzing the functioning of the Applications, optimizing and
        improving them, creating a personalized browsing experience for you,
        evaluating the performance of our marketing and advertising activities,
        and to present you with personalized marketing content, including on
        third-party websites and services. This is all for the purposes detailed
        in this privacy policy. &nbsp; Although there is no change in the way we
        operate in response to the &quot;Do Not Track&quot; check box in the
        browser&apos;s HTTP header field, most browsers allow you to control
        cookies, including choosing whether to accept or reject such files and
        whether to remove existing files. In most browsers, you can define if
        you want to receive an update for each cookie file, or that such files
        will be temporarily or permanently blocked.&nbsp;
      </p>
      <h2>
        <b>7. How will we get in touch with you?</b>
      </h2>
      <ul>
        <li>
          &nbsp; &nbsp; Service and Operation Notices &ndash; We may contact you
          with important information regarding our services or your candidacy
          for our training courses. For example, we may contact you by email,
          phone, text message or any other means available to us to provide you
          with information and materials at your request, conduct a telephone
          interview with you or invite you to a screening day at our offices.
        </li>
        <li>
          &nbsp; &nbsp; Marketing inquiries &ndash; Subject to obtaining your
          prior consent in accordance with the law, we may contact you with
          messages of a marketing or advertising nature, for example regarding
          our services, promotions, events, special offers, and any other
          information that we think may be of interest to you. Such notices may
          be sent by email, text message, or any other means available to us to
          contact you. If you are not interested in receiving such advertising
          from us, you may request to be removed from our mailing lists at any
          time by contacting info@infinitylabs.co.il , or by acting in
          accordance with the instructions that will appear in the messages sent
          to you regarding &quot;removal from the mailing list&quot;,
          &quot;termination&quot;, &quot;change of email preferences&quot;, etc.
        </li>
        <li>
          &nbsp; &nbsp; A friend-brings-a friend referral &ndash; We may contact
          you with an offer to apply for our training courses, if your friend or
          company (such as current students or graduates of our training
          courses, or our business partners) gave us your contact information
          and declared to us that they received your consent in advance so that
          we could contact you for this purpose.
        </li>
      </ul>
      <h2>
        <b>8. How is the information secured?</b>
      </h2>
      <p>
        We take physical, technological and organizational security measures in
        order to adequately protect the information stored by us and by our
        service providers, in accordance with legal requirements and accepted
        standards in the market (including encryption in appropriate cases).
        However, it is important that you are aware that regardless of the
        security measures taken, today it is not possible to completely secure
        information and accordingly, ILRD cannot guarantee absolute protection
        for the information stored by it or by service providers on its behalf
        against any malfunction, hacking, infiltration or any other
        misuse.&nbsp;
      </p>
      <h2>
        <b>9. How can you exercise your rights in relation to information?</b>
      </h2>
      <p>
        If you wish to exercise your rights, according to law, to review the
        information concerning you that is kept by us, and in applicable cases,
        to request that such information be corrected, you may do so by
        contacting the email info@infinitylabs.co.il . If necessary, we may ask
        you to provide additional details and documents so that we can verify
        your identity and ensure that such information will only be disclosed to
        the person to whom it pertains. Details and additional documents as
        stated, as well as your correspondence with us regarding your request,
        will be kept by us for legal purposes (such as documentation of the
        identity verification of the applicant or the applicant&rsquo;s request
        to review the information) in accordance with what is stated in section
        5. &nbsp; For your attention, the rights granted to you by law for
        information concerning you is not absolute. Accordingly, there may be
        situations in which we will be entitled or obligated to refuse to
        provide you with the requested information, in whole or in part,
        together with the reasons, as stated by law.&nbsp;
      </p>
      <h2>
        <b>10. General notices and contact information</b>
      </h2>
      <ul>
        <li>
          &nbsp; &nbsp; Updates and revisions &ndash; This privacy policy may be
          updated from time to time by posting a revised version of it on the
          Applications&rsquo; websites . The updated version will be valid from
          the date of publication. However, if we believe that such an update or
          amendment includes substantial changes, advance notice will be given
          on the website or through other appropriate means of communication
          available to us. At the end of the period specified in that notice,
          all amendments will be deemed to be agreed upon by you.
        </li>
        <li>
          &nbsp; &nbsp; Links to External Sites &ndash; Although the ILRD Sites
          may include links to the websites of third parties and the services
          they offer , such as our customers and business partners, ILRD is not
          responsible for how third parties treat your information. Such
          external websites and any information that may be provided or
          processed through them are subject to the privacy policy and terms of
          use of those websites, and not to this privacy policy. We encourage
          you to pay attention when leaving the Applications in favor of other
          websites, and to carefully read the privacy policies and terms of use
          of those websites and external parties.
        </li>
        <li>
          &nbsp; &nbsp; Contact &ndash; If you have comments or questions
          regarding this privacy policy or your privacy in the information kept
          by us, if you have concerns about this or if you want to file a
          complaint about it, you can contact us by email at
          info@infinitylabs.co.il .
        </li>
      </ul>
      <p>&nbsp;</p>
      <h2>
        <b>
          11. Limited Use disclosure that complies with the Google API Services
          User Data Policy
        </b>
      </h2>
      <p>
        The Applications use and transfer to any other app of information
        received from Google APIs will adhere to the
        <a
          href='https://developers.google.com/terms/api-services-user-data-policy#additional_requirements_for_specific_api_scopes'
          target='_blank'
          rel='noreferrer'
        >
          Google API Services User Data Policy
        </a>
        , including the Limited Use requirements.
      </p>
      <h2>
        <b>12. For mobile application users:</b>
      </h2>
      <p>
        <a href={mobilePrivacyPolicyLink} target='_blank' rel='noreferrer'>
          Link to privacy policy for mobile application users.
        </a>
      </p>
    </DocumentPage>
  );
};

export default PrivacyPolicy;
